import {
  isComparisonMode,
  isNotComparisonMode,
  showAllComparisonMode,
} from '../utils/comparison-mode-options'
import {
  filters,
  SyncsInformationCategories,
  SyncsInformationScopes,
  comparisonModes,
} from '../constants'
import { getComparisonLabel, normalizeSettingsForShowAll } from './shared'

function formatSyncStatusForExport(value) {
  if (!value || typeof value === 'string') {
    return value || ''
  }

  const statusLabel = I18n.t(
    `companies_overview.filters.syncs_information.status_values.${value.status}`
  )

  const parts = [statusLabel]

  if (value.timestamp) {
    parts.push(value.timestamp)
  }

  if (value.error_message) {
    parts.push(value.error_message)
  }

  return parts.join(' - ')
}

function columnPrefix(category) {
  if (category === SyncsInformationCategories.administration) {
    return I18n.t(
      'companies_overview.filters.syncs_information.column_prefix.administration'
    )
  }
  return ''
}

function joinWithPrefix(prefix, ...parts) {
  return [prefix, ...parts].filter(Boolean).join(' ')
}

export const syncsInformationDefinition = {
  type: filters.SYNCS_INFORMATION,
  nameKey: 'syncs_information',
  deployDate: '2026-04-01',
  ledgerRequired: false,

  searchableAttributes() {
    const attributeLabel = (category, key) =>
      I18n.t(
        `companies_overview.filters.syncs_information.searchable_attributes.${category}.${key}`
      )

    return Object.values(SyncsInformationCategories).flatMap((category) =>
      Object.values(SyncsInformationScopes).map((scopeType) => ({
        settings: { category, scopeType },
        label: attributeLabel(category, scopeType),
      }))
    )
  },

  comparisonModes() {
    return [
      ...showAllComparisonMode(),
      ...isComparisonMode(),
      ...isNotComparisonMode(),
    ]
  },

  getColumnTitle(settings) {
    const prefix = columnPrefix(settings.category)
    const scopeLabel = I18n.t(
      `companies_overview.filters.syncs_information.scopes.${settings.scopeType}`
    )
    return joinWithPrefix(prefix, scopeLabel)
  },

  activeSubtitle(settings) {
    const {
      category,
      scopeType,
      comparisonMode,
      filterDataValue,
      filterDataLabel,
    } = settings
    const comparisonLabel = getComparisonLabel(
      comparisonMode,
      this.comparisonModes()
    )
    const prefix = columnPrefix(category)
    const scopeLabel = I18n.t(
      `companies_overview.filters.syncs_information.scopes.${scopeType}`
    )

    if (comparisonMode === comparisonModes.SHOW_ALL) {
      return joinWithPrefix(prefix, scopeLabel, comparisonLabel)
    }

    let valueLabel = filterDataLabel || filterDataValue || ''

    if (scopeType === SyncsInformationScopes.sync_status) {
      valueLabel = I18n.t(
        `companies_overview.filters.syncs_information.status_values.${filterDataValue}`
      )
    }

    return joinWithPrefix(prefix, scopeLabel, comparisonLabel, valueLabel)
  },

  normalizeSettings(settings) {
    return normalizeSettingsForShowAll(settings, 'filterDataValue', null)
  },

  serializeSettings(settings) {
    return {
      category: settings.category || SyncsInformationCategories.bookkeeping,
      comparison_mode: settings.comparisonMode,
      scope_type: settings.scopeType,
      filter_data_value: settings.filterDataValue,
      filter_data_label: settings.filterDataLabel,
    }
  },

  deserializeSettings(params) {
    return {
      category: params.category || SyncsInformationCategories.bookkeeping,
      comparisonMode: params.comparison_mode,
      scopeType: params.scope_type,
      filterDataValue: params.filter_data_value,
      filterDataLabel: params.filter_data_label,
    }
  },

  formatForExport(value) {
    return formatSyncStatusForExport(value)
  },

  sortValue(value) {
    if (value && typeof value === 'object') {
      return value.status || ''
    }
    return value || ''
  },
}
